import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { GET_ROAD_BY_ID } from '../../../../gql/queries/geoPositions/roads';
import { FETCH_PREDEFINED_OPTIONS_ACCIDENTS } from '../../../../gql/queries/accidents/predefinedData';

// Utils
import { formAccidentNaming } from '../../../../components/forms/AddBasicInfoAccidentForm/constants';
import { AuthContextType } from '../../../../hooks/useAuth';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';
import { claimDraftScope } from '../../../../utils/draftOwnership';
import {
  ACCIDENT_CREATION_SCOPE,
  ACCIDENT_MEDIA_SCOPE,
  accidentCreationOwnerId,
} from '../../../../utils/draftScopes';
import { readAccidentCreationDraft } from '../../../../utils/accidentCreationDraft';

// Types
import { type Accident } from '../../../../types/accident';

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const { data } = await client.query({
        query: FETCH_PREDEFINED_OPTIONS_ACCIDENTS,
      });

      return {
        ...data,
      };
    }
    return {};
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Accident Summary loading error:', err);
    return true;
  }
};

export const AddBasicInfoAccidentLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async ({ params }) => {
  // 🔴 Dueño al llegar (S4; spec 2026-11-02 wizard-draft-ownership, I1): el
  // alta es de la OT/campaña de la URL, no de la que diga el borrador. Ver
  // `NewAccidentLoader`.
  const owner = accidentCreationOwnerId(params.id);
  claimDraftScope(ACCIDENT_CREATION_SCOPE, owner);
  claimDraftScope(ACCIDENT_MEDIA_SCOPE, owner);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  const stringifyForm = sessionStorage.getItem(
    formAccidentNaming.ADD_BASIC_INFO_ACCIDENT_FORM
  );

  // El padre sale de la URL: la copia del paso 1 solo vale si es suya. Antes
  // se tomaba `orderParentId` de la copia, fuera de quien fuera (auditoría
  // § 4). Sin el paso 1 de este padre no hay alta que continuar: `null` y la
  // pantalla vuelve atrás.
  const accidentData = readAccidentCreationDraft(params.id) as Accident | null;
  if (!accidentData) return null;

  let road;
  const { orderParentRoid, orderParentId, registerInitDate } = accidentData;
  const loaderToResolve = await loadFormOptions(userData);
  try {
    const roadResult = await client.query({
      query: GET_ROAD_BY_ID,
      variables: {
        id: accidentData.road,
      },
    });
    road = roadResult.data.road.name;
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Accident error - Error getting road');
  }

  if (stringifyForm && userData && orderParentRoid && orderParentId) {
    const sessionStorageData = JSON.parse(
      decompressSessionStorage(stringifyForm)
    );

    const combinedData: Accident = {
      ...sessionStorageData,
      orderParentRoid,
      orderParentId,
      road,
      registerInitDate,
      ...loaderToResolve,
    };

    return combinedData;
  }

  return {
      orderParentRoid,
      orderParentId,
      registerInitDate,
      road,
      ...loaderToResolve,
    };
};

  return { data: load() };
};
